import { useState, useEffect, useContext, useMemo } from "react";
import { Auth } from "aws-amplify";
import { recordLogoutAudit } from "util/logoutAudit";
import { logError } from "util/logging";
import { LoggedInUserContext } from "contexts/LoggedInUser";
import { getAvailableRoles, highestUserRole } from "util/role";
import { useLocation } from "react-router-dom";

/**
 * Contains the logic for Header
 *
 * @version 0.0.1
 * @author Steve Hunley
 *
 */

const useHeader = () => {
  const [userName, setUserName] = useState("");
  const { loggedInUserProfile } = useContext(LoggedInUserContext);

  useEffect(() => {
    authenticate(setUserName);
  }, []);
 
  const SIDEBAR_ROUTES = {
    DASHBOARD: [
      "/dashboards",
      "/dashboardVisualization",
    ],
    QUERY: ["/openquery", "/queryvisualization"],
    AWARE: ["/aware"],
    BRAND: ["/UserBrands", "/BrandAdmin"],
    HELP_DOC: ["/help"],
    ACCOUNT: ["/UserAccounts", "/AccountAdmin", "/ContractAdmin", "/admin/mfa-recovery"],
    USER: ["/User", "/UserAdmin"]
  };

  const HEADER_TITLES = {
    DASHBOARD: {
      title: "DASHBOARDS",
      redirectTo: "/dashboards"
    },
    QUERY: {
      title: "QUERY",
      redirectTo: "/openquery"
    },
    AWARE: {
      title: "AWARE",
      redirectTo: "/aware"
    },
    BRAND: {
      title: "BRAND",
      redirectTo: "/UserBrands"
    },
    HELP_DOC: {
      title: "HELP DOCUMENTS",
      redirectTo: "/help"
    },
    ACCOUNT: {
      title: "ACCOUNT",
      redirectTo: "/UserAccounts"
    },
    USER: {
      title: "MY USER",
      redirectTo: "/User"
    },

  };

  const matchPath = (currentPath, routes = []) =>
    routes.some(
      route => currentPath === route || currentPath.startsWith(route + "/")
    );

  const useHeaderTitle = () => {
    const { pathname } = useLocation();

    if (matchPath(pathname, SIDEBAR_ROUTES.DASHBOARD)) {
      return HEADER_TITLES.DASHBOARD;
    }

    if (matchPath(pathname, SIDEBAR_ROUTES.QUERY)) {
      return HEADER_TITLES.QUERY;
    }

    if (matchPath(pathname, SIDEBAR_ROUTES.AWARE)) {
      return HEADER_TITLES.AWARE;
    }

    if (matchPath(pathname, SIDEBAR_ROUTES.BRAND)) {
      return HEADER_TITLES.BRAND;
    }

    if (matchPath(pathname, SIDEBAR_ROUTES.HELP_DOC)) {
      return HEADER_TITLES.HELP_DOC;
    }

    if (matchPath(pathname, SIDEBAR_ROUTES.USER)) {
      return HEADER_TITLES.USER;
    }

    if (matchPath(pathname, SIDEBAR_ROUTES.ACCOUNT)) {
      return HEADER_TITLES.ACCOUNT;
    }

    return "";
  };

  const availableRoles = useMemo(() => {
    if (!loggedInUserProfile?.accounts?.length) {
      return [];
    }
    return getAvailableRoles(highestUserRole(loggedInUserProfile.accounts.map(account => account.role)));
  }, [loggedInUserProfile]);

  const signOut = () => {
    handleSignOut();
  };

  return { userName, signOut, availableRoles, useHeaderTitle };
};

const handleSignOut = async () => {
  try {
    await recordLogoutAudit("manual");
    await Auth.signOut({ global: true });
    window.location.href = "/";
  } catch (error) {
    console.log("error signing out: ", error);
  }
};

const authenticate = setUserName => {
  Auth.currentAuthenticatedUser()
    .then(user => {
      setUserName(user.attributes.name || user.attributes.email);
    })
    .catch(e => {
      logError("HeaderView error", e);
      setUserName("");
    });
};

export default useHeader;
